/**
 * LoyaltyPunchCard · v2.7.65+E259
 * ===============================
 * Shared 10-ride loyalty punch-card. Two exports:
 *   • LoyaltyPunchCard({ data, name }) — pure presentational card.
 *   • RiderLoyaltyCard({ name })       — self-fetching wrapper (calls
 *     GET /api/customer/loyalty via customerApi); renders nothing while
 *     loading or if the rider isn't authed. Drop-in for the Rider dashboard.
 *
 * Used by the public /loyalty page AND the logged-in Rider dashboard so both
 * show the exact same card.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Car, Gift, ArrowRight, Check, KeyRound } from "lucide-react";
import customerApi from "@/lib/customerApi";

const AMBER = "#fbbf24";

export function LoyaltyPunchCard({ data, name = "Rider" }) {
  const freeEvery = data.free_every || 10;
  const progress = data.progress || 0;            // 0..freeEvery-1
  const completed = data.completed_rides || 0;
  const earned = data.free_rides_earned || 0;
  const nextFree = !!data.next_ride_is_free;
  const untilFree = data.rides_until_free || freeEvery;

  const cells = [];
  for (let i = 0; i < freeEvery; i += 1) {
    cells.push({ i, isReward: i === freeEvery - 1, stamped: i < progress });
  }

  return (
    <div className="rounded-2xl border-2 p-6 sm:p-8" style={{ borderColor: AMBER, backgroundColor: "#111113" }} data-testid="loyalty-card">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: "#a1a1aa" }}>Your Loyalty Card</div>
          <div className="text-2xl font-black" style={{ color: "#f4f4f5" }} data-testid="loyalty-card-name">Hi, {name}</div>
        </div>
        <div className="text-right">
          <div className="text-3xl font-black leading-none" style={{ color: AMBER }} data-testid="loyalty-progress-count">{progress} / {freeEvery}</div>
          <div className="text-[11px] uppercase tracking-wide mt-1" style={{ color: "#a1a1aa" }}>stamps this card</div>
        </div>
      </div>

      <div className="rounded-xl px-4 py-3 mb-6 text-center" style={{ backgroundColor: nextFree ? "rgba(251,191,36,0.16)" : "rgba(39,39,42,0.7)", border: `1px solid ${nextFree ? AMBER : "#3f3f46"}` }} data-testid="loyalty-status">
        {nextFree ? (
          <div data-testid="loyalty-freeride-nudge">
            <span className="font-black text-base" style={{ color: AMBER }}>🎉 Your NEXT Weatherford ride is FREE!</span>
            <div className="text-[11px] font-semibold mt-1" style={{ color: "#d4d4d8" }}>
              Book a ride within Weatherford city limits to redeem — it's on us, no charge.
            </div>
          </div>
        ) : (
          <span className="font-bold text-sm" style={{ color: "#e4e4e7" }}>
            <span className="font-black" style={{ color: AMBER }}>{untilFree}</span> more {untilFree === 1 ? "ride" : "rides"} until your free ride
          </span>
        )}
      </div>

      <div className="grid grid-cols-5 gap-2.5 sm:gap-3" data-testid="loyalty-punchcard">
        {cells.map((c) => {
          if (c.isReward) {
            const rewardReady = nextFree;
            return (
              <div
                key={c.i}
                className="aspect-square rounded-xl flex flex-col items-center justify-center"
                style={{ backgroundColor: rewardReady ? AMBER : "rgba(251,191,36,0.12)", border: `2px dashed ${AMBER}` }}
                data-testid="loyalty-cell-reward"
              >
                <Gift className="h-6 w-6" style={{ color: rewardReady ? "#111113" : AMBER }}/>
                <span className="text-[10px] font-black mt-1" style={{ color: rewardReady ? "#111113" : AMBER }}>FREE</span>
              </div>
            );
          }
          return (
            <div
              key={c.i}
              className="aspect-square rounded-xl flex flex-col items-center justify-center"
              style={{ backgroundColor: c.stamped ? "rgba(251,191,36,0.18)" : "#18181b", border: `1px solid ${c.stamped ? AMBER : "#3f3f46"}` }}
              data-testid={`loyalty-cell-${c.i}`}
            >
              {c.stamped ? (
                <>
                  <Car className="h-5 w-5" style={{ color: AMBER }}/>
                  <Check className="h-3 w-3 -mt-0.5" style={{ color: AMBER }}/>
                </>
              ) : (
                <span className="text-sm font-black" style={{ color: "#52525b" }}>{c.i + 1}</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mt-6 pt-5 border-t border-zinc-800">
        <div className="text-sm" style={{ color: "#a1a1aa" }}>
          Lifetime completed rides: <span className="font-black" style={{ color: "#f4f4f5" }} data-testid="loyalty-lifetime">{completed}</span>
        </div>
        {earned > 0 && (
          <div className="text-sm font-bold" style={{ color: AMBER }} data-testid="loyalty-earned">
            {earned} free {earned === 1 ? "ride" : "rides"} earned so far 🎉
          </div>
        )}
      </div>

      <div className="mt-6 text-center">
        <Link to="/ride/login" data-testid="loyalty-card-book">
          <Button className="bg-amber-400 hover:bg-amber-300 text-zinc-950 font-extrabold uppercase tracking-wider">
            Book Your Next Ride <ArrowRight className="h-4 w-4 ml-1.5"/>
          </Button>
        </Link>
      </div>
    </div>
  );
}

/**
 * CompactLoyalty — slim, unobtrusive one-row tracker for the Rider dashboard.
 * Shows a mini progress bar + "N more rides to a free ride" (or the free-ride
 * nudge). Keeps the key testids so existing checks still pass.
 */
function CompactLoyalty({ data }) {
  const freeEvery = data.free_every || 10;
  const progress = data.progress || 0;
  const nextFree = !!data.next_ride_is_free;
  const untilFree = data.rides_until_free || freeEvery;
  const pct = Math.min(100, Math.round((progress / freeEvery) * 100));
  return (
    <div
      className="mt-4 rounded-xl border px-3 py-2.5 flex items-center gap-3"
      style={{ borderColor: nextFree ? AMBER : "#3f3f46", backgroundColor: "#111113" }}
      data-testid="rider-loyalty-card"
    >
      <Gift className="h-4 w-4 shrink-0" style={{ color: AMBER }}/>
      {nextFree ? (
        <div className="flex-1 min-w-0 text-xs font-bold" style={{ color: AMBER }} data-testid="loyalty-freeride-nudge">
          🎉 Your next Weatherford ride is FREE — book to redeem
        </div>
      ) : (
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="text-[11px] font-semibold truncate" style={{ color: "#d4d4d8" }} data-testid="loyalty-until-free">
              <span className="font-black" style={{ color: AMBER }}>{untilFree}</span> more {untilFree === 1 ? "ride" : "rides"} to a free ride
            </span>
            <span className="text-[10px] font-black tabular-nums shrink-0" style={{ color: AMBER }} data-testid="loyalty-progress-count">{progress}/{freeEvery}</span>
          </div>
          <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: "#27272a" }}>
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: AMBER }} data-testid="loyalty-progress-bar"/>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * RiderLoyaltyCard — fetches the rider's own progress and renders the card.
 * Renders nothing while loading or if the request fails (e.g. not authed),
 * so it's safe to drop into the dashboard without extra guards.
 * Pass `compact` for the slim below-the-form tracker.
 */
export function RiderLoyaltyCard({ name = "Rider", compact = false }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    customerApi.get("/customer/loyalty")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);
  if (!data) return null;
  if (compact) return <CompactLoyalty data={data}/>;
  return (
    <div className="mb-6" data-testid="rider-loyalty-card">
      <LoyaltyPunchCard data={data} name={name}/>
    </div>
  );
}

export function RiderFreeRideHistory() {
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    customerApi.get("/customer/loyalty/history")
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData(null); });
    return () => { cancelled = true; };
  }, []);
  if (!data || (data.earned_total === 0 && data.redeemed_count === 0)) return null;

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return ""; }
  };
  const stats = [
    { label: "Earned", value: data.earned_total, tid: "freeride-earned" },
    { label: "Redeemed", value: data.redeemed_count, tid: "freeride-redeemed" },
    { label: "Available", value: data.available, tid: "freeride-available" },
  ];

  return (
    <div className="mb-6 rounded-2xl border p-5 sm:p-6" style={{ borderColor: "#3f3f46", backgroundColor: "#111113" }} data-testid="rider-freeride-history">
      <div className="flex items-center gap-2 mb-4">
        <Gift className="h-5 w-5" style={{ color: AMBER }}/>
        <h3 className="text-lg font-black" style={{ color: "#f4f4f5" }}>Free-Ride Rewards</h3>
      </div>
      <div className="grid grid-cols-3 gap-3 mb-5">
        {stats.map((s) => (
          <div key={s.label} className="rounded-xl px-3 py-3 text-center" style={{ backgroundColor: "#18181b", border: "1px solid #27272a" }} data-testid={s.tid}>
            <div className="text-2xl font-black" style={{ color: AMBER }}>{s.value}</div>
            <div className="text-[10px] uppercase tracking-widest font-bold mt-0.5" style={{ color: "#a1a1aa" }}>{s.label}</div>
          </div>
        ))}
      </div>
      {data.available > 0 && (
        <div className="rounded-lg px-3 py-2.5 mb-4 text-center text-sm font-bold" style={{ backgroundColor: "rgba(251,191,36,0.14)", border: `1px solid ${AMBER}`, color: AMBER }} data-testid="freeride-available-note">
          🎉 You have {data.available} free {data.available === 1 ? "ride" : "rides"} to use — your next Weatherford ride is on us!
        </div>
      )}

      {/* Redeem-at-Pickup PIN — the single-use code the driver/dispatch confirms. */}
      {data.active_pin && (
        <div
          className="rounded-xl px-4 py-4 mb-4 text-center"
          style={{ backgroundColor: "rgba(251,191,36,0.10)", border: `2px dashed ${AMBER}` }}
          data-testid="freeride-pin-card"
        >
          <div className="flex items-center justify-center gap-1.5 mb-1">
            <KeyRound className="h-4 w-4" style={{ color: AMBER }}/>
            <span className="text-[10px] uppercase tracking-[0.25em] font-black" style={{ color: "#a1a1aa" }}>Your Free-Ride Code</span>
          </div>
          <div className="text-4xl font-black font-mono tracking-[0.25em] tabular-nums" style={{ color: AMBER }} data-testid="freeride-pin-code">
            {data.active_pin}
          </div>
          <div className="text-[11px] font-semibold mt-2" style={{ color: "#d4d4d8" }}>
            Give this code to your driver (or dispatch) at pickup to redeem your free ride. One-time use.
          </div>
        </div>
      )}

      <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: "#a1a1aa" }}>Redeemed free rides</div>
      {data.redeemed.length === 0 ? (
        <div className="text-sm" style={{ color: "#71717a" }} data-testid="freeride-none">None yet — book your next Weatherford ride to redeem.</div>
      ) : (
        <div className="space-y-2" data-testid="freeride-redeemed-list">
          {data.redeemed.map((r) => (
            <div key={r.id} className="rounded-lg px-3 py-2 flex items-center justify-between gap-3" style={{ backgroundColor: "#18181b", border: "1px solid #27272a" }} data-testid={`freeride-item-${r.id}`}>
              <div className="min-w-0">
                <div className="text-xs font-bold truncate" style={{ color: "#e4e4e7" }}>
                  {r.pickup || r.dropoff ? `${r.pickup || "—"} → ${r.dropoff || "—"}` : "Free ride redeemed"}
                </div>
                <div className="text-[10px]" style={{ color: "#71717a" }}>
                  {fmt(r.date)}{r.pin ? ` · code ${r.pin}` : ""}{r.redeemed_by_role ? ` · by ${r.redeemed_by_role}` : ""}
                </div>
              </div>
              <span className="text-[9px] uppercase tracking-wider px-2 py-0.5 rounded-full font-black flex-shrink-0 inline-flex items-center gap-1" style={{ backgroundColor: "rgba(16,185,129,0.2)", color: "#6ee7b7", border: "1px solid rgba(52,211,153,0.6)" }}>
                <Check className="h-3 w-3"/> REDEEMED
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default LoyaltyPunchCard;
